import {type RunsDataQueryConfig} from '../../../containers/RunsDataLoader';
import * as Filters from '../../../util/filters';
import {type Filter, type IndividualFilter} from '../../../util/filterTypes';
import {type Key} from '../../../util/runTypes';

function intersectSets(left: Set<string>, right: Set<string>): Set<string> {
  return new Set(Array.from(left).filter(value => right.has(value)));
}

function setsAreEqual(left: Set<string>, right: Set<string>): boolean {
  return (
    left.size === right.size &&
    Array.from(left).every(value => right.has(value))
  );
}

function getRunNameConstraint(
  filter: IndividualFilter<Key>
): Set<string> | null {
  if (filter.key.section !== 'run' || filter.key.name !== 'name') {
    return null;
  }
  if (filter.op === '=' && typeof filter.value === 'string') {
    return new Set([filter.value]);
  }
  if (
    filter.op === 'IN' &&
    filter.value.every((value): value is string => typeof value === 'string')
  ) {
    return new Set(filter.value);
  }
  return null;
}

function getConstrainedRunNames(filter: Filter<Key>): Set<string> | null {
  if (filter.disabled === true) {
    return null;
  }

  if (Filters.isIndividual(filter)) {
    return getRunNameConstraint(filter);
  }

  const childConstraints = filter.filters.map(getConstrainedRunNames);

  if (filter.op === 'OR') {
    return childConstraints.every(
      (constraint): constraint is Set<string> => constraint != null
    )
      ? new Set(childConstraints.flatMap(constraint => [...constraint]))
      : null;
  }

  return childConstraints
    .filter((constraint): constraint is Set<string> => constraint != null)
    .reduce<Set<string> | null>(
      (constraint, childConstraint) =>
        constraint == null
          ? childConstraint
          : intersectSets(constraint, childConstraint),
      null
    );
}

function alreadyFiltersToRequiredRuns(
  filter: Filter<Key>,
  requiredRunNames: string[]
): boolean {
  const constrainedRunNames = getConstrainedRunNames(filter);
  return (
    constrainedRunNames != null &&
    setsAreEqual(constrainedRunNames, new Set(requiredRunNames))
  );
}

/**
 * Ensures configured runs are hydrated even when pagination would omit them.
 *
 * Runs-low has no "page plus required runs" primitive, so this expresses the
 * union as the normal query plus one same-runset query filtered to those runs.
 */
export function includeRequiredRunQueries(
  queryConfig: RunsDataQueryConfig,
  requiredRunNames: string[]
): void {
  if (requiredRunNames.length === 0) {
    return;
  }

  const requiredRunsFilter = Filters.runNamesFilter(requiredRunNames);
  queryConfig.runsetQueries = queryConfig.runsetQueries.flatMap(runsetQuery => [
    runsetQuery,
    ...(alreadyFiltersToRequiredRuns(runsetQuery.filters, requiredRunNames)
      ? []
      : [
          {
            ...runsetQuery,
            filters: Filters.And([runsetQuery.filters, requiredRunsFilter]),
            selections: Filters.MATCH_NONE_FILTER,
          },
        ]),
  ]);
}
